// SubscriptionPaymentSummary.tsx
import React from 'react';
import { Box, Typography, Stack, Divider } from '@mui/material';

interface PermitSubscription {
  PermitID: number;
  PermitName: string;
  Period: 'Yearly' | 'Monthly';
  Price: number;
}

interface SubscriptionPaymentSummaryProps {
  subscriptionDetails: PermitSubscription[];
  taxRate?: number;
}

const SubscriptionPaymentSummary: React.FC<SubscriptionPaymentSummaryProps> = ({
  subscriptionDetails,
  taxRate = 2 // Default tax rate of 2%
}) => {
  // Calculate subtotal
  const subtotal = subscriptionDetails?.reduce((acc, permit) => acc + permit.Price, 0);
  
  // Calculate tax amount
  const taxAmount = (subtotal * taxRate) / 100;
  
  // Calculate total with tax
  const total = subtotal + taxAmount;

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        alignItems: 'center',
        borderRadius: 2,
        boxShadow: 3,
        padding: 3,
        mt: 4,
        border: '1px solid #e0e0e0',
        backgroundColor: '#ffffff'
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
        Permit Subscription Summary
      </Typography>

      {/* Permit Details */}
      {subscriptionDetails.map((permit, index) => (
        <React.Fragment key={permit.PermitID}>
          <Stack
            direction="column"
            sx={{ width: '100%', mb: 0 }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              sx={{ width: '100%' }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 500 }}>
                  {permit.PermitName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {permit.Period === 'Yearly' ? 'Annual Subscription' : 'Monthly Subscription'}
                </Typography>
              </Box>
              <Typography sx={{ ml: 2, fontWeight: 500 }}>
                ${permit.Price.toFixed(2)}
              </Typography>
            </Stack>
          </Stack>
          {index < subscriptionDetails.length - 1 && (
            <Divider sx={{ width: '100%', my: 2 }} />
          )}
        </React.Fragment>
      ))}

      {/* Summary */}
      <Box sx={{ width: '100%', mt: 3 }}>
        <Divider sx={{ mb: 2 }} />
        
        {/* Subtotal */}
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{ width: '100%', mb: 1 }}
        >
          <Typography>Subtotal:</Typography>
          <Typography>${subtotal.toFixed(2)}</Typography>
        </Stack>

        {/* Tax */}
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{ width: '100%', mb: 1 }}
        >
          <Typography>Processing Fee ({taxRate}%):</Typography>
          <Typography>${taxAmount.toFixed(2)}</Typography>
        </Stack>

        {/* Total */}
        <Divider sx={{ my: 2 }} />
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{ width: '100%' }}
        >
          <Typography sx={{ fontWeight: 600 }}>Total Amount:</Typography>
          <Typography sx={{ fontWeight: 600 }}>
            ${total.toFixed(2)}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

export default SubscriptionPaymentSummary;